import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import RouteSeo from "./seo/RouteSeo";
import NotFoundPage from "./pages/NotFoundPage";
import PropertyBrowsingPage from "./pages/PropertyBrowsingPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import CreatePropertyPage from "./pages/CreatePropertyPage";
import EditPropertyPage from "./pages/EditPropertyPage";
import HostDashboard from "./pages/HostDashboard";
import GuestDashboard from "./pages/GuestDashboard";
import BecomeHostPage from "./pages/BecomeHostPage";
import AddRolePage from "./pages/AddRolePage";
import HostOnboarding from "./pages/HostOnboarding";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import CompanySignupPage from "./pages/CompanySignupPage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import BookingSuccessPage from "./pages/BookingSuccessPage";
import BookingCancelPage from "./pages/BookingCancelPage";
import ProviderDashboard from "./pages/ProviderDashboard";
import LandlordDashboard from "./pages/LandlordDashboard";
import HostDealMarketplace from "./pages/HostDealMarketplace";
import RestaurantDashboard from "./pages/RestaurantDashboard";
import RestaurantSignupPage from "./pages/RestaurantSignupPage";
import DriverDashboard from "./pages/DriverDashboard";
import DriverSignupPage from "./pages/DriverSignupPage";
import DeliveryRiderSignupPage from "./pages/DeliveryRiderSignupPage";
import LandlordSignupPage from "./pages/LandlordSignupPage";
import ServiceProviderSignupPage from "./pages/ServiceProviderSignupPage";
import DeliveryRiderDashboard from "./pages/DeliveryRiderDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import RestrictedBusinessesPage from "./pages/RestrictedBusinessesPage";
import SellerPaymentsAndRiskPage from "./pages/SellerPaymentsAndRiskPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmergencyResetPage from "./pages/EmergencyResetPage";
import AdminBootstrapPage from "./pages/AdminBootstrapPage";
import AdminMaintenancePage from "./pages/AdminMaintenancePage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import CleaningRespondPage from "./pages/CleaningRespondPage";
import AccountPrivacyPage from "./pages/AccountPrivacyPage";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { captureAcquisition, syncAcquisitionWithConsent } from "./utils/acquisition";
import InstallPrompt from "./components/InstallPrompt";
import ResponsibilityAcknowledgementGate from "./components/ResponsibilityAcknowledgementGate";
import MessagesPage from "./pages/MessagesPage";
import SharedTrackingPage from "./pages/SharedTrackingPage";
import ServiceMarketplacePage from "./pages/ServiceMarketplacePage";
import PublicProviderProfilePage from "./pages/PublicProviderProfilePage";
import ServiceRequestPage from "./pages/ServiceRequestPage";
import MyServiceRequestsPage from "./pages/MyServiceRequestsPage";
import AboutPage from "./pages/AboutPage";
import CareersPage from "./pages/CareersPage";
import HostFeeAlternativePage from "./pages/HostFeeAlternativePage";
import LowCommissionManagementPage from "./pages/LowCommissionManagementPage";
import LocalCleaningHubPage from "./pages/LocalCleaningHubPage";
import LocalCleaningPage from "./pages/LocalCleaningPage";
import ContactPage from "./pages/ContactPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import TrustPage from "./pages/TrustPage";
import ProfitCalculatorPage from "./pages/ProfitCalculatorPage";
import HostResourcesPage from "./pages/HostResourcesPage";
import { Toaster } from "./components/ui/sonner";
import { useAuth } from "./contexts/AuthContext";

/* ─────────────────────────────────────────────────────────────
   ADMIN ENFORCER
   Runs on every navigation. If the authenticated user has the
   admin role and is on a non-admin route, they are immediately
   sent to the admin console — no modes, no consumer dashboards.
───────────────────────────────────────────────────────────── */
const ADMIN_ROUTE = "/admin/dashboard";
// Routes the enforcer leaves alone (login, public pages, the admin console itself,
// and the admin's own profile / security settings — the admin still needs to
// manage their own 2FA and password, otherwise the mandatory-admin-2FA gate is
// unreachable for any newly promoted admin).
const BYPASS_PREFIXES = [
  "/admin",
  "/login",
  "/signup",
  "/about",
  "/careers",
  "/contact",
  "/how-it-works",
  "/help",
  "/terms",
  "/trust",
  "/airbnb-host-fee-alternative",
  "/low-commission-airbnb-management",
  "/airbnb-cleaning",   // hub + per-city pages
  "/track",
  "/property/",   // public property detail
  "/settings",    // admin's own profile + 2FA + password change
  "/profile",     // alias for /settings
];

function AdminEnforcer() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;                              // still initialising
    if (!user?.roles?.includes("admin")) return;     // not an admin — leave alone

    const path = location.pathname;
    const isBypass = BYPASS_PREFIXES.some(p => path === p || path.startsWith(p));
    if (!isBypass) {
      // Admin is on a consumer/guest/host page — send them home
      navigate(ADMIN_ROUTE, { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);

  return null; // renders nothing — purely a side-effect component
}

/* Prerender readiness. Pages start "not ready" (window.prerenderReady=false
   set in index.html) so the prerender service waits for content before it
   snapshots. Data-driven routes (property detail) flip it to true themselves
   once their data has loaded; every other route is ready as soon as it paints.
   Real browsers ignore this flag. */
function PrerenderReadySignal() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const DATA_ROUTES = ["/property/"];
    const needsData = DATA_ROUTES.some((p) => location.pathname.startsWith(p));
    if (needsData) {
      window.prerenderReady = false;
      return;
    }
    const t = setTimeout(() => { window.prerenderReady = true; }, 50);
    return () => clearTimeout(t);
  }, [location.pathname]);
  return null; // side-effect only
}

/* Partner tracking links (utm_*). Picks up the tags when a visitor lands,
   and keeps their storage in line with the cookie choice — see
   utils/acquisition.js. Side-effect only. */
function AcquisitionCapture() {
  const location = useLocation();
  useEffect(() => {
    captureAcquisition();
  }, [location.search]);
  useEffect(() => {
    const onConsent = (e) => syncAcquisitionWithConsent(e.detail);
    window.addEventListener("cookie-consent-change", onConsent);
    return () => window.removeEventListener("cookie-consent-change", onConsent);
  }, []);
  return null;
}

/* ─────────────────────────────────────────────────────────────
   ROLE WRAPPERS  (unchanged)
───────────────────────────────────────────────────────────── */
function RestaurantDashboardWrapper() {
  const { user, getAuthToken, logout } = useAuth();
  return <RestaurantDashboard token={getAuthToken()} user={user} onLogout={logout} />;
}

function DeliveryRiderDashboardWrapper() {
  const { user, getAuthToken, logout } = useAuth();
  return <DeliveryRiderDashboard token={getAuthToken()} user={user} onLogout={logout} />;
}

/* ─────────────────────────────────────────────────────────────
   APP
───────────────────────────────────────────────────────────── */
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          {/* AdminEnforcer sits inside BrowserRouter so it can use
              useNavigate/useLocation, but outside Routes so it always runs */}
          <AdminEnforcer />
          <PrerenderReadySignal />
          <AcquisitionCapture />
          <ResponsibilityAcknowledgementGate />

          <Routes>
            {/* ── Public ── */}
            <Route path="/"                        element={<PropertyBrowsingPage />} />
            <Route path="/property/:propertyId"    element={<PropertyDetailPage />} />
            <Route path="/about"                   element={<AboutPage />} />
            <Route path="/careers"                 element={<CareersPage />} />
            <Route path="/contact"                 element={<ContactPage />} />
            <Route path="/how-it-works"            element={<HowItWorksPage />} />
            <Route path="/help"                    element={<HelpCenterPage />} />
            <Route path="/trust"                   element={<TrustPage />} />
            <Route path="/airbnb-host-fee-alternative"      element={<HostFeeAlternativePage />} />
            <Route path="/low-commission-airbnb-management" element={<LowCommissionManagementPage />} />
            <Route path="/airbnb-cleaning"                  element={<LocalCleaningHubPage />} />
            <Route path="/airbnb-cleaning/:city"            element={<LocalCleaningPage />} />
            <Route path="/terms"                   element={<TermsPage />} />
            <Route path="/privacy"                 element={<PrivacyPage />} />
            <Route path="/cookies"                 element={<CookiePolicyPage />} />
            <Route path="/restricted-businesses"   element={<RestrictedBusinessesPage />} />
            <Route path="/host/payments-and-risk"  element={<SellerPaymentsAndRiskPage />} />
            <Route path="/forgot-password"         element={<ForgotPasswordPage />} />
            <Route path="/reset-password"          element={<ResetPasswordPage />} />
            <Route path="/emergency-reset"         element={<EmergencyResetPage />} />
            <Route path="/admin-bootstrap"         element={<AdminBootstrapPage />} />
            <Route path="/verify-email"            element={<VerifyEmailPage />} />
            <Route path="/unsubscribe"             element={<UnsubscribePage />} />
            <Route path="/cleaning/respond"        element={<CleaningRespondPage />} />
            <Route path="/account/privacy"         element={<AccountPrivacyPage />} />
            <Route path="/track/:shareToken"       element={<SharedTrackingPage />} />

            {/* ── Auth ── */}
            <Route path="/login"                   element={<LoginPage />} />
            <Route path="/signup"                  element={<SignupPage />} />
            <Route path="/signup/company"          element={<CompanySignupPage />} />

            {/* ── Admin console ── */}
            <Route path="/admin/dashboard"         element={<AdminDashboard />} />
            <Route path="/admin/maintenance"       element={<AdminMaintenancePage />} />

            {/* ── Host ── */}
            <Route path="/host/dashboard"                      element={<HostDashboard />} />
            <Route path="/host/create-property"                element={<CreatePropertyPage />} />
            <Route path="/host/edit-property/:propertyId"      element={<EditPropertyPage />} />
            <Route path="/host/onboarding"                     element={<HostOnboarding />} />
            <Route path="/host/deals/marketplace"              element={<HostDealMarketplace />} />
            <Route path="/host/calculator"                     element={<ProfitCalculatorPage />} />
            <Route path="/host/resources"                      element={<HostResourcesPage />} />
            <Route path="/become-host"                         element={<BecomeHostPage />} />
            <Route path="/add-role"                            element={<AddRolePage />} />

            {/* ── Guest ── */}
            <Route path="/guest/bookings"          element={<GuestDashboard />} />
            <Route path="/guest/browse"            element={<GuestDashboard />} />
            <Route path="/booking/success"         element={<BookingSuccessPage />} />
            <Route path="/booking/cancel"          element={<BookingCancelPage />} />

            {/* ── Other portals ── */}
            <Route path="/provider/dashboard"      element={<ProviderDashboard />} />
            <Route path="/landlord/dashboard"      element={<LandlordDashboard />} />
            <Route path="/restaurant"              element={<Navigate to="/restaurant/signup" replace />} />
            <Route path="/restaurant/signup"       element={<RestaurantSignupPage />} />
            <Route path="/restaurant/dashboard"    element={<RestaurantDashboardWrapper />} />
            <Route path="/driver/signup"           element={<DriverSignupPage />} />
            <Route path="/driver/dashboard"        element={<DriverDashboard />} />
            <Route path="/delivery/signup"         element={<DeliveryRiderSignupPage />} />
            <Route path="/delivery/dashboard"      element={<DeliveryRiderDashboardWrapper />} />
            <Route path="/landlord/signup"         element={<LandlordSignupPage />} />
            <Route path="/provider/signup"         element={<ServiceProviderSignupPage />} />

            {/* ── Shared / services ── */}
            <Route path="/messages"                element={<MessagesPage />} />
            <Route path="/settings"                element={<ProfileSettingsPage />} />
            <Route path="/profile"                 element={<ProfileSettingsPage />} />
            <Route path="/services"                element={<ServiceMarketplacePage />} />
            <Route path="/provider/:providerId"    element={<PublicProviderProfilePage />} />
            <Route path="/services/request"        element={<ServiceRequestPage />} />
            <Route path="/services/my-requests"    element={<MyServiceRequestsPage />} />

            {/* Header.jsx's admin button goes to /admin, which never had a
                route (it showed a blank page). */}
            <Route path="/admin"                   element={<Navigate to={ADMIN_ROUTE} replace />} />

            {/* Unknown URLs: a real "not found" page that tells crawlers
                (noindex) instead of a blank 200. Keep last. */}
            <Route path="*"                        element={<NotFoundPage />} />
          </Routes>
          {/* Per-page title / description / canonical for public pages
              (seo/pageMeta.js). Renders nothing. */}
          <RouteSeo />
          <CookieConsentBanner />
          <InstallPrompt />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
